<template>
  <div
    class="fill-height d-flex flex-column"
    style="width: 100%"
  >
    <div class="d-flex justify-end mb-6 align-center">
      <v-tooltip
        :text="t('raster.tooltip.reset_bbox')"
        bottom
        :open-delay="200"
        style="z-index: 99999"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="outlined"
            color="error"
            data-testid="control-panel-bbox-delete"
            style="padding: 0; min-width: 36px; margin-left: 12px"
            width="36px"
            @click="gridStore.resetBbox()"
          >
            <v-icon color="error">
              mdi-delete-outline
            </v-icon>
          </v-btn>
        </template>
      </v-tooltip>
    </div>
    <div v-show="inputHelpIsActive">
      <div class="d-flex justify-center">
        <div
          class="d-flex flex-column align-center"
          style="width: 400px"
        >
          <v-text-field
            v-model="northString"
            class="coord-input"
            data-testid="north-input"
            variant="filled"
            :label="t('north')"
            suffix="°"
            :rules="[isValidNorth]"
            @input="(event) => gridStore.changeBboxNorth(event.target.value)"
          />
          <div
            class="d-flex justify-space-between align-start"
            style="width: 100%; height: 100px; margin-top: 20px"
          >
            <v-text-field
              v-model="westString"
              class="coord-input"
              data-testid="west-input"
              variant="filled"
              :label="t('west')"
              suffix="°"
              :rules="[isValidWest]"
              @input="(event) => gridStore.changeBboxWest(event.target.value)"
            />
            <v-text-field
              v-model="eastString"
              class="coord-input"
              data-testid="east-input"
              variant="filled"
              :label="t('east')"
              suffix="°"
              :rules="[isValidEast]"
              @input="(event) => gridStore.changeBboxEast(event.target.value)"
            />
          </div>
          <v-text-field
            v-model="southString"
            class="coord-input"
            data-testid="south-input"
            variant="filled"
            :label="t('south')"
            suffix="°"
            :rules="[isValidSouth]"
            @input="(event) => gridStore.changeBboxSouth(event.target.value)"
          />
        </div>
      </div>
    </div>
    <div v-show="!inputHelpIsActive">
      <v-text-field
        v-model="textualBboxString"
        variant="filled"
        data-testid="bbox-text-input"
        persistent-hint
        class="mt-4"
        :label="t('raster.bbox_control.text_field_label')"
        :hint="t('raster.bbox_control.text_field_hint')"
        :rules="[isValidBbox]"
        @input="(event) => gridStore.changeTextualBbox(event.target.value)"
      />
    </div>
    <v-spacer />
    <div class="d-flex justify-space-between">
      <v-checkbox
        v-model="inputHelpIsActive"
        data-testid="bbox-input-help"
        :label="t('raster.input_help_label')"
      />
      <v-select
        v-model="selectedPreset"
        style="max-width: 250px; height: 40px;"
        :items="bboxPresetChoices"
        item-title="label"
        item-value="value"
        variant="outlined"
        density="compact"
        hide-details
        data-testid="bbox-preset-choices"
        :label="t('raster.tooltip.template_selection')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from "vue";
import GridStore from "@/stores/grid/grid-module";
import RasterDatasetStore from "@/stores/dataset/raster-dataset-module";
import { isCoordinate } from "@/stores/timeseries/timeseries-model";
import { LatLngBounds } from "leaflet";
import { Bundesland } from "@/api/out";
import { useI18n } from "vue-i18n";

type BundeslandKey = keyof typeof Bundesland;

export default defineComponent ({
  name: 'GridCoordinateDialog',
  setup() {
    const { t } = useI18n();

    const inputHelpIsActive = ref(true);
    const gridStore = GridStore;
    const rasterDatasetStore = RasterDatasetStore;
    const selectedPreset = ref<BundeslandKey | null>(null);

    const northString = ref(gridStore.bboxNorth ?? '');
    const southString = ref(gridStore.bboxSouth ?? '');
    const westString = ref(gridStore.bboxWest ?? '');
    const eastString = ref(gridStore.bboxEast ?? '');
    const textualBboxString = ref(gridStore.textualBbox ?? '');

    const bboxPresets = computed<Record<BundeslandKey, LatLngBounds>>(() => ({
      Niedersterreich: new LatLngBounds(
        [47.414444, 14.416944],
        [49.024722, 17.198333]
      ),
      Burgenland: new LatLngBounds(
        [46.8248, 15.96667],
        [48.144722, 17.24792]
      ),
      Wien: new LatLngBounds(
        [48.115278, 16.175833],
        [48.333056, 16.631944]
      ),
      Obersterreich: new LatLngBounds(
        [47.421389, 12.73],
        [48.776944, 15.036111]
      ),
      Steiermark: new LatLngBounds(
        [46.554722, 13.520278],
        [47.85634, 16.203056]
      ),
      Krnten: new LatLngBounds(
        [46.356111, 12.644722],
        [47.171111, 15.215]
      ),
      Salzburg: new LatLngBounds(
        [46.9335, 12.034167],
        [48.047222, 14.02]
      ),
      Tirol: new LatLngBounds(
        [46.599958, 10.088611],
        [47.748056, 12.970278]
      ),
      Vorarlberg: new LatLngBounds(
        [46.822224, 9.37749],
        [47.654167, 10.266667]
      ),
    }));

    const bboxPresetChoices = computed(() =>
      Object.keys(bboxPresets.value)
        .sort()
        .map((key) => ({
          value: key,
          label: t(`state.${key}`)
        }))
    );

    const currentBbox = computed<LatLngBounds | null>(() => gridStore.leafletBbox);

    watch(selectedPreset, (newVal) => {
      if (newVal) {
        gridStore.updateBbox(bboxPresets.value[newVal]);
      }
    });

    watch(currentBbox, (newVal) => {
      if (selectedPreset.value && newVal) {
        if (!newVal.equals(bboxPresets.value[selectedPreset.value])) {
          selectedPreset.value = null;
        }
      }
    });

    // adhoc function used for v-text-field 'rules' prop
    const isValidSouth = (x: string): string | boolean => {
      if (!isCoordinate(x)) return t("raster.invalid_south");
      const xNumber = parseFloat(x);
      if (!rasterDatasetStore.isLatInsideBounds(xNumber))
        return t("raster.south_out_of_bounds");
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidWest = (x: string): string | boolean => {
      if (!isCoordinate(x)) return t("raster.invalid_west");
      const xNumber = parseFloat(x);
      if (!rasterDatasetStore.isLonInsideBounds(xNumber))
        return t("raster.west_out_of_bounds");
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidNorth = (x: string): string | boolean => {
      if (!isCoordinate(x)) return t("raster.invalid_north");
      const xNumber = parseFloat(x);
      if (!rasterDatasetStore.isLatInsideBounds(xNumber))
        return t("raster.north_out_of_bounds");
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidEast = (x: string): string | boolean => {
      if (!isCoordinate(x)) return t("raster.invalid_east");
      const xNumber = parseFloat(x);
      if (!rasterDatasetStore.isLonInsideBounds(xNumber))
        return t("raster.east_out_of_bounds");
      return true;
    };

    // adhoc function used for v-text-field 'rules' prop
    const isValidBbox = (x: any): string | boolean => {
      if (typeof x !== "string") return t("raster.invalid_bbox");
      const xSplit = x.split(",");
      if (xSplit.length !== 4) return t("raster.invalid_bbox");

      const southCheck = isValidSouth(xSplit[0]);
      if (southCheck !== true) return southCheck;
      const westCheck = isValidWest(xSplit[1]);
      if (westCheck !== true) return westCheck;
      const northCheck = isValidNorth(xSplit[2]);
      if (northCheck !== true) return northCheck;
      const eastCheck = isValidEast(xSplit[3]);
      if (eastCheck !== true) return eastCheck;

      return true;
    };

    watch(
      () => gridStore.leafletBbox,
      (newVal) => {
        if (!newVal) {
          northString.value = '';
          southString.value = '';
          westString.value = '';
          eastString.value = '';
          textualBboxString.value = '';
        } else {
          northString.value = gridStore.bboxNorth ?? '';
          southString.value = gridStore.bboxSouth ?? '';
          westString.value = gridStore.bboxWest ?? '';
          eastString.value = gridStore.bboxEast ?? '';
          textualBboxString.value = gridStore.textualBbox ?? '';
        }
      }
    );

    return {
      t,
      inputHelpIsActive,
      gridStore,
      rasterDatasetStore,
      selectedPreset,
      bboxPresets,
      bboxPresetChoices,
      currentBbox,
      isValidSouth,
      isValidWest,
      isValidNorth,
      isValidEast,
      isValidBbox,
      northString,
      southString,
      westString,
      eastString,
      textualBboxString,
    };
  },
});
</script>

<style scoped>
.coord-input {
  width: 160px !important;
  max-width: 160px !important;
}
</style>
